import { useEffect } from "react";
import { useParams } from "react-router-dom";
import type { ClientWithMembership } from "../types";
import { firstCompany } from "./use-clients";

const PREFERRED_COMPANY_KEY = "preferred_company_id";

const readPreferredCompanyId = (): string | null =>
  localStorage.getItem(PREFERRED_COMPANY_KEY);

/**
 * The company to land on: the last one opened when the user still belongs to
 * it, else the default one. Every entry point without a company in its path
 * (`/`, `/start`, `/home`) goes through here, or the app reopens on the first
 * company of the list whatever was open last.
 */
export const landingCompany = (
  clients: ClientWithMembership[],
): ClientWithMembership | undefined => {
  const preferred = readPreferredCompanyId();
  return (
    clients.find((c) => c.client.id === preferred) || firstCompany(clients)
  );
};

export function usePreferredCompany(): {
  preferredCompanyId: string | null;
  setPreferredCompanyId: (companyId: string) => void;
} {
  const { company: currentCompanyId } = useParams<{ company: string }>();

  // Update preferred company whenever the URL company changes
  useEffect(() => {
    if (currentCompanyId) {
      localStorage.setItem(PREFERRED_COMPANY_KEY, currentCompanyId);
    }
  }, [currentCompanyId]);

  const preferredCompanyId = currentCompanyId || readPreferredCompanyId();

  const setPreferredCompanyId = (companyId: string) => {
    localStorage.setItem(PREFERRED_COMPANY_KEY, companyId);
  };

  return {
    preferredCompanyId,
    setPreferredCompanyId,
  };
}
